import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faCircleExclamation } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Checkbox, Chip } from "@performyard/py-components";
import type { ReviewCycleReportRowFrontend, ReviewCycleReportStatus } from "@performyard/service-contracts";
import { DEFAULT_TIMEZONE, isValidIanaTimezone } from "@performyard/utils-isomorphic";

type ChipColor = "primary" | "neutral" | "red" | "green" | "yellow";

type StatusDisplay = {
	label: string;
	color: ChipColor;
	icon?: IconDefinition;
};

const DUE_DATE_FORMAT = "MMM d, yyyy";

/**
 * Formats a due date in the company timezone. Due dates are a company-timezone concept but are
 * stored as absolute instants, and the shared `type: "date"` column formats in the viewer's browser
 * timezone, which shows the adjacent day when the two disagree.
 */
const formatDueDateInCompanyTimezone = (value: unknown, companyTimezone: string): string => {
	if (value === null || value === undefined || value === "") return "";

	const date = value instanceof Date ? value : new Date(value as string);
	if (Number.isNaN(date.getTime())) return "";

	const displayTimezone = isValidIanaTimezone(companyTimezone) ? companyTimezone : "UTC";
	return formatInTimeZone(date, displayTimezone, DUE_DATE_FORMAT);
};

// Overdue carries an icon so it is distinguishable from the same-colored Closed chip.
const STATUS_DISPLAY: Record<ReviewCycleReportStatus, StatusDisplay> = {
	active: { label: "In Progress", color: "primary" },
	scheduled: { label: "Scheduled", color: "yellow" },
	complete: { label: "Completed", color: "green" },
	overdue: { label: "Overdue", color: "red", icon: faCircleExclamation },
	closed: { label: "Closed", color: "red" },
};

type ReportValueGetterParams = ValueGetterParams<ReviewCycleReportRowFrontend>;
type ReportCellRendererParams = ICellRendererParams<ReviewCycleReportRowFrontend>;

const ChipCell = ({ label, color, icon }: StatusDisplay) => (
	<div className="flex h-full items-center">
		<Chip label={label} color={color} size={24} icon={icon} />
	</div>
);

const StatusChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data?.status) return null;
	return <ChipCell {...STATUS_DISPLAY[params.data.status]} />;
};

const VisibilityChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data) return null;
	return <ChipCell label={params.data.visibleToSubject ? "Visible" : "Not Visible"} color="neutral" />;
};

const RedactedChipCellRenderer = (params: ReportCellRendererParams) => {
	if (!params.data) return null;
	return <ChipCell label={params.data.redacted ? "Yes" : "No"} color="neutral" />;
};

export type SelectionColumnOptions = {
	isRowSelected: (_rowId: string) => boolean;
	onToggleRow: (_rowId: string, _selected: boolean) => void;
	/** True when every row the search is currently showing is selected */
	allFilteredRowsSelected: boolean;
	onToggleFilteredRows: (_selected: boolean) => void;
	/** No rows to select, so the header checkbox is inert */
	hasRows: boolean;
};

/**
 * Read through a ref rather than taking the options directly, so the column defs stay
 * referentially stable as the selection changes.
 */
type SelectionColumnOptionsRef = { current: SelectionColumnOptions };

export const SELECTION_COLUMN_ID = "selection";

const SELECTION_COLUMN_WIDTH = 48;

/**
 * Leading checkbox column. Selection lives outside the grid (keyed by report id) so it survives
 * the reports stream appending rows and the search filter hiding them.
 *
 * The renderers read the current selection out of a ref instead of closing over it. Re-supplying
 * columnDefs would re-apply the `sort` in each def below and snap the table back to the default
 * sort, so the consumer keeps these defs stable and calls refreshCells/refreshHeader instead.
 */
const getSelectionColumn = (optionsRef: SelectionColumnOptionsRef): ColDef<ReviewCycleReportRowFrontend> => ({
	colId: SELECTION_COLUMN_ID,
	headerName: "",
	sortable: false,
	resizable: false,
	suppressSizeToFit: true,
	width: SELECTION_COLUMN_WIDTH,
	minWidth: SELECTION_COLUMN_WIDTH,
	maxWidth: SELECTION_COLUMN_WIDTH,
	headerComponent: () => (
		<Checkbox
			id="review-forms-select-visible"
			aria-label="Select all forms shown"
			checked={optionsRef.current.allFilteredRowsSelected}
			disabled={!optionsRef.current.hasRows}
			onCheckedChange={(checked) => optionsRef.current.onToggleFilteredRows(!!checked)}
		/>
	),
	cellRenderer: (params: ReportCellRendererParams) => {
		const rowId = params.data?._id;
		if (!rowId) return null;

		return (
			<div className="flex h-full items-center">
				<Checkbox
					id={`review-form-select-${rowId}`}
					aria-label={`Select ${params.data?.form?.name ?? "form"} for ${params.data?.subject?.name ?? "subject"}`}
					checked={optionsRef.current.isRowSelected(rowId)}
					onCheckedChange={(checked) => optionsRef.current.onToggleRow(rowId, !!checked)}
				/>
			</div>
		);
	},
});

export const getReviewFormsColumns = ({
	companyTimezone = DEFAULT_TIMEZONE,
	selectionRef,
}: {
	companyTimezone?: string;
	selectionRef?: SelectionColumnOptionsRef;
} = {}): ColDef<ReviewCycleReportRowFrontend>[] => [
	...(selectionRef ? [getSelectionColumn(selectionRef)] : []),
	{
		colId: "formName",
		headerName: "Form Name",
		type: "text",
		valueGetter: (params: ReportValueGetterParams) => params.data?.form?.name ?? "",
		sortable: true,
		flex: 1,
	},
	{
		field: "subject",
		colId: "subjectName",
		headerName: "Review Subject",
		type: "employee",
		sortable: true,
		flex: 1,
	},
	{
		colId: "status",
		headerName: "Form Status",
		cellRenderer: StatusChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) =>
			params.data?.status ? STATUS_DISPLAY[params.data.status].label : "",
		sortable: true,
		flex: 1,
	},
	{
		field: "authorDueDate",
		headerName: "Author Due Date",
		type: "date",
		// Overrides the shared date column's formatter, which renders in the browser's timezone
		valueFormatter: (params: ValueFormatterParams<ReviewCycleReportRowFrontend>) =>
			formatDueDateInCompanyTimezone(params.value, companyTimezone),
		sortable: true,
		flex: 1,
	},
	{
		field: "signerDueDate",
		headerName: "Signer Due Date",
		type: "date",
		valueFormatter: (params: ValueFormatterParams<ReviewCycleReportRowFrontend>) =>
			formatDueDateInCompanyTimezone(params.value, companyTimezone),
		sortable: true,
		flex: 1,
	},
	{
		colId: "visibleToSubject",
		headerName: "Visible to Subject",
		cellRenderer: VisibilityChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) => (params.data?.visibleToSubject ? "Visible" : "Not Visible"),
		sortable: true,
		flex: 1,
	},
	{
		colId: "redacted",
		headerName: "Author Redaction",
		cellRenderer: RedactedChipCellRenderer,
		valueGetter: (params: ReportValueGetterParams) => (params.data?.redacted ? "Yes" : "No"),
		sortable: true,
		flex: 1,
	},
];
